import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 25_000; // 25s — feels real-time without hammering the backend
const MUTE_STORAGE_KEY = 'notifications_muted';

/** Tiny Web-Audio beep so we don't need to ship an mp3 asset. */
let _audioCtx = null;
const playChime = () => {
  try {
    _audioCtx ||= new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;
    const tones = [880, 1320]; // soft two-note chirp
    tones.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + i * 0.12);
      gain.gain.linearRampToValueAtTime(0.12, now + i * 0.12 + 0.02);
      gain.gain.linearRampToValueAtTime(0, now + i * 0.12 + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.2);
    });
  } catch {
    /* audio unavailable — silent fail */
  }
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unseenCount, setUnseenCount] = useState(0);
  const [unseenCoachComments, setUnseenCoachComments] = useState(0);
  const [unseenAthleteComments, setUnseenAthleteComments] = useState(0);
  const [unseenMessages, setUnseenMessages] = useState(0);
  // Bumps each time the polling cycle detects a new coach-assigned workout
  // OR a new coach comment. Pages can use it as an effect dependency to
  // refetch their data without having to wire up websockets.
  const [dataChangeStamp, setDataChangeStamp] = useState(0);
  const [muted, setMutedState] = useState(() => {
    try { return localStorage.getItem(MUTE_STORAGE_KEY) === '1'; } catch { return false; }
  });
  const toastShownRef = useRef(false);
  const commentsToastShownRef = useRef(false);
  const messagesToastShownRef = useRef(false);
  const prevCoachCommentsRef = useRef(0);
  const prevAthleteCommentsRef = useRef(0);
  const prevUnseenRef = useRef(0);
  const prevMessagesRef = useRef(0);

  const setMuted = useCallback((value) => {
    setMutedState(value);
    try { localStorage.setItem(MUTE_STORAGE_KEY, value ? '1' : '0'); } catch { /* noop */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      setUnseenCount(0);
      setUnseenCoachComments(0);
      setUnseenAthleteComments(0);
      setUnseenMessages(0);
      return null;
    }

    // Admin: poll athlete comments + athlete-replied messages.
    if (user.role === 'admin') {
      try {
        const [commentsRes, msgsRes] = await Promise.all([
          axios.get(`${API}/admin/comments/unseen-count`),
          axios.get(`${API}/admin/messages/unread-count`),
        ]);
        const n = commentsRes.data?.count || 0;
        if (n > prevAthleteCommentsRef.current && prevAthleteCommentsRef.current > 0 && !muted) {
          playChime();
          toast(
            n === 1
              ? 'Tienes un nuevo comentario de un deportista'
              : `Tienes ${n} comentarios nuevos de deportistas`,
            { duration: 7000 }
          );
        }
        prevAthleteCommentsRef.current = n;
        setUnseenAthleteComments(n);

        const m = msgsRes.data?.unread || 0;
        if (m > prevMessagesRef.current && prevMessagesRef.current > 0 && !muted) {
          playChime();
          toast(
            m === 1
              ? 'Un deportista te ha respondido'
              : `${m} deportistas te han respondido`,
            { duration: 7000 }
          );
        }
        prevMessagesRef.current = m;
        setUnseenMessages(m);
        return { athleteComments: n, athleteMessages: m };
      } catch {
        return null;
      }
    }

    // Athlete: poll new workouts + new coach comments + new coach messages.
    try {
      const [unseenRes, commentsRes, msgsRes] = await Promise.all([
        axios.get(`${API}/workouts/unseen`),
        axios.get(`${API}/workouts/unseen-coach-comments`),
        axios.get(`${API}/messages/unread-count`),
      ]);
      const count = unseenRes.data?.count || 0;
      setUnseenCount(count);
      // Detect transitions: if the count grew vs the previous tick we have
      // brand new coach-assigned workouts to show on the calendar/dashboard.
      const hasNewWorkouts = count > prevUnseenRef.current;
      if (count > 0 && !toastShownRef.current) {
        toastShownRef.current = true;
        const items = unseenRes.data?.items || [];
        const firstTitle = items[0]?.title || '';
        if (!muted) playChime();
        toast(
          count === 1
            ? `Coach Alex te ha asignado 1 nuevo entrenamiento`
            : `Coach Alex te ha asignado ${count} nuevos entrenamientos`,
          {
            description: firstTitle ? `Más reciente: ${firstTitle}` : undefined,
            duration: 8000,
          }
        );
      } else if (count > prevUnseenRef.current && prevUnseenRef.current > 0 && !muted) {
        // Polling cycle detected NEW workouts assigned during the session.
        playChime();
        toast(`Coach Alex te ha asignado entrenamientos nuevos`, { duration: 6000 });
      }
      prevUnseenRef.current = count;

      const ccount = commentsRes.data?.count || 0;
      setUnseenCoachComments(ccount);
      const hasNewComments = ccount > prevCoachCommentsRef.current;
      if (ccount > 0 && !commentsToastShownRef.current) {
        commentsToastShownRef.current = true;
        const item = (commentsRes.data?.items || [])[0];
        const preview = item?.from_coach
          ? (item?.message || '').slice(0, 90)
          : (item?.reply || '').slice(0, 90);
        if (!muted) playChime();
        toast(
          ccount === 1
            ? `Tienes un nuevo comentario del coach`
            : `Tienes ${ccount} nuevos comentarios del coach`,
          {
            description: preview ? `“${preview}${preview.length >= 90 ? '…' : ''}”` : undefined,
            duration: 9000,
          }
        );
      } else if (ccount > prevCoachCommentsRef.current && prevCoachCommentsRef.current > 0 && !muted) {
        // Real-time detection while user is on the app.
        playChime();
        toast('Nuevo comentario del coach', { duration: 6000 });
      }
      prevCoachCommentsRef.current = ccount;

      const mcount = msgsRes.data?.unread || 0;
      setUnseenMessages(mcount);
      if (mcount > 0 && !messagesToastShownRef.current) {
        messagesToastShownRef.current = true;
        if (!muted) playChime();
        toast(
          mcount === 1
            ? 'Tienes un mensaje nuevo del coach'
            : `Tienes ${mcount} mensajes nuevos del coach`,
          { duration: 8000 }
        );
      } else if (mcount > prevMessagesRef.current && prevMessagesRef.current > 0 && !muted) {
        playChime();
        toast('Nuevo mensaje del coach', { duration: 6000 });
      }
      prevMessagesRef.current = mcount;

      // Single bump per poll cycle if anything changed → drives passive
      // refetch on CalendarPage / Dashboard without web sockets.
      if (hasNewWorkouts || hasNewComments) {
        setDataChangeStamp((s) => s + 1);
      }

      return { unseen: unseenRes.data, coachComments: commentsRes.data, messages: mcount };
    } catch {
      return null;
    }
  }, [user, muted]);

  const markSeen = useCallback(async () => {
    if (!user || user.role === 'admin' || unseenCount === 0) return;
    try {
      await axios.post(`${API}/workouts/mark-seen`);
      setUnseenCount(0);
      prevUnseenRef.current = 0;
    } catch {
      /* ignore */
    }
  }, [user, unseenCount]);

  const markCoachCommentsSeen = useCallback(async () => {
    if (!user || user.role === 'admin' || unseenCoachComments === 0) return;
    try {
      await axios.post(`${API}/workouts/mark-coach-comments-seen`);
      setUnseenCoachComments(0);
      prevCoachCommentsRef.current = 0;
    } catch {
      /* ignore */
    }
  }, [user, unseenCoachComments]);

  // Initial fetch + polling
  useEffect(() => {
    refresh();
    if (!user) return;
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [user, refresh]);

  // Reset the toast flags when the logged-in user changes
  useEffect(() => {
    toastShownRef.current = false;
    commentsToastShownRef.current = false;
    messagesToastShownRef.current = false;
    prevCoachCommentsRef.current = 0;
    prevAthleteCommentsRef.current = 0;
    prevUnseenRef.current = 0;
    prevMessagesRef.current = 0;
  }, [user?.id]);

  // Silent Strava auto-sync on app entry + window focus.
  // The backend enforces a 15-min throttle, so we can be safe to retrigger
  // on every focus event. We refresh notifications afterwards so any newly
  // matched / merged workouts surface immediately on the dashboard/calendar.
  const autoSyncStravaRef = useRef(0);
  useEffect(() => {
    if (!user || user.role === 'admin') return;

    const runAutoSync = async () => {
      // Client-side guard: never fire more than once per minute regardless
      // of how many focus events happen.
      const now = Date.now();
      if (now - autoSyncStravaRef.current < 60_000) return;
      autoSyncStravaRef.current = now;
      try {
        const res = await axios.post(`${API}/integrations/strava/auto-sync`);
        const data = res?.data || {};
        if (data.status === 'synced') {
          const matched = data.matched || 0;
          const synced = data.synced || 0;
          if (matched + synced > 0) {
            // Trigger refetch on Dashboard / Calendar listeners.
            setDataChangeStamp((s) => s + 1);
            if (!muted) {
              const parts = [];
              if (synced > 0) parts.push(`${synced} nuevo${synced === 1 ? '' : 's'} de Strava`);
              if (matched > 0) parts.push(`${matched} enlazado${matched === 1 ? '' : 's'}`);
              toast.success('Strava sincronizado', {
                description: parts.join(' · '),
                duration: 5000,
              });
            }
          }
        }
      } catch {
        /* silent — auto-sync should never disrupt the UX */
      }
    };

    runAutoSync();
    const onFocus = () => runAutoSync();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // Narrow deps on purpose: `user` changes object identity on every /auth/me
    // refresh; re-running the auto-sync then would spam Strava. id/role cover it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, muted]);

  return (
    <NotificationContext.Provider
      value={{
        unseenCount,
        unseenCoachComments,
        unseenAthleteComments,
        unseenMessages,
        dataChangeStamp,
        muted,
        setMuted,
        refresh,
        markSeen,
        markCoachCommentsSeen,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    return {
      unseenCount: 0,
      unseenCoachComments: 0,
      unseenAthleteComments: 0,
      unseenMessages: 0,
      dataChangeStamp: 0,
      muted: false,
      setMuted: () => {},
      refresh: () => {},
      markSeen: () => {},
      markCoachCommentsSeen: () => {},
    };
  }
  return ctx;
};
